'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { X, ChevronRight, Loader2, Check } from 'lucide-react';

interface AddProductDrawerProps {
  open: boolean;
  onClose: () => void;
}

const tabs = [
  { id: 'tab-basic', label: 'Basic Info' },
  { id: 'tab-pricing', label: 'Pricing & Inventory' },
  { id: 'tab-media', label: 'Media' },
  { id: 'tab-variations', label: 'Variations' },
  { id: 'tab-status', label: 'Status' },
];

interface ProductFormData {
  name: string;
  sku: string;
  barcode: string;
  brand: string;
  category: string;
  productType: string;
  shortDesc: string;
  regularPrice: string;
  salePrice: string;
  costPrice: string;
  stockQty: string;
  lowStockThreshold: string;
  status: string;
  featured: boolean;
}

export default function AddProductDrawer({ open, onClose }: AddProductDrawerProps) {
  const [activeTab, setActiveTab] = useState('tab-basic');
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ProductFormData>({
    defaultValues: {
      productType: 'simple',
      status: 'draft',
      featured: false,
    },
  });

  // Backend integration: POST /api/seller/products
  const onSubmit = async (data: ProductFormData) => {
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 1200));
    setSubmitting(false);
    setSaved(true);
    setTimeout(() => { setSaved(false); reset(); onClose(); }, 1500);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 fade-in" onClick={onClose} />

      {/* Drawer */}
      <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-card shadow-modal flex flex-col slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div>
            <h2 className="text-lg font-semibold">Add New Product</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Fill in product details across tabs</p>
          </div>
          <button onClick={onClose} className="btn-ghost p-2">
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border shrink-0 overflow-x-auto scrollbar-thin">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-xs font-semibold whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Form content */}
        <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto scrollbar-thin">
          <div className="px-6 py-5 space-y-5">

            {/* Tab: Basic Info */}
            {activeTab === 'tab-basic' && (
              <>
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1">
                    Product Name <span className="text-danger">*</span>
                  </label>
                  <p className="text-2xs text-muted-foreground mb-1.5">Use a clear, descriptive name with brand and key specs</p>
                  <input
                    {...register('name', { required: 'Product name is required', minLength: { value: 10, message: 'Name must be at least 10 characters' } })}
                    className="input-field"
                    placeholder="e.g. Samsung Galaxy A55 5G — 8GB/256GB Midnight Black"
                  />
                  {errors.name && <p className="text-xs text-danger mt-1">{errors.name.message}</p>}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1">
                      SKU <span className="text-danger">*</span>
                    </label>
                    <p className="text-2xs text-muted-foreground mb-1.5">Unique stock keeping unit identifier</p>
                    <input
                      {...register('sku', { required: 'SKU is required' })}
                      className="input-field font-mono"
                      placeholder="SAM-A55-256-BLK"
                    />
                    {errors.sku && <p className="text-xs text-danger mt-1">{errors.sku.message}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1">Barcode (EAN/UPC)</label>
                    <p className="text-2xs text-muted-foreground mb-1.5">Optional — for inventory scanning</p>
                    <input
                      {...register('barcode')}
                      className="input-field font-mono"
                      placeholder="8801643......"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1">
                      Brand <span className="text-danger">*</span>
                    </label>
                    <select
                      {...register('brand', { required: 'Brand is required' })}
                      className="input-field cursor-pointer"
                    >
                      <option value="">Select brand...</option>
                      {['Samsung', 'Apple', 'Sony', 'HP', 'Dell', 'Haier', 'Logitech', 'Xiaomi', 'JBL', 'Canon', 'Anker', 'Corsair'].map((b) => (
                        <option key={`form-brand-${b.toLowerCase()}`} value={b.toLowerCase()}>{b}</option>
                      ))}
                    </select>
                    {errors.brand && <p className="text-xs text-danger mt-1">{errors.brand.message}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1">
                      Category <span className="text-danger">*</span>
                    </label>
                    <select
                      {...register('category', { required: 'Category is required' })}
                      className="input-field cursor-pointer"
                    >
                      <option value="">Select category...</option>
                      {['Electronics', 'Mobile & Tablets', 'Laptops & PCs', 'Audio & Headphones', 'Cameras', 'Accessories', 'Home Appliances', 'Monitors'].map((c) => (
                        <option key={`form-cat-${c.toLowerCase().replace(/\s+/g, '-')}`} value={c.toLowerCase().replace(/\s+/g, '-')}>{c}</option>
                      ))}
                    </select>
                    {errors.category && <p className="text-xs text-danger mt-1">{errors.category.message}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1">Product Type</label>
                  <div className="flex gap-3">
                    {[{ value: 'simple', label: 'Simple Product' }, { value: 'variable', label: 'Variable Product' }].map((pt) => (
                      <label key={`ptype-${pt.value}`} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          value={pt.value}
                          {...register('productType')}
                          className="accent-primary"
                        />
                        <span className="text-xs font-medium">{pt.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1">Short Description</label>
                  <textarea
                    {...register('shortDesc')}
                    rows={3}
                    className="input-field resize-none"
                    placeholder="Brief product summary shown in listing cards (max 160 chars)..."
                  />
                </div>
              </>
            )}

            {/* Tab: Pricing & Inventory */}
            {activeTab === 'tab-pricing' && (
              <>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1">
                      Regular Price (PKR) <span className="text-danger">*</span>
                    </label>
                    <input
                      {...register('regularPrice', { required: 'Regular price is required' })}
                      type="number"
                      className="input-field tabular-nums"
                      placeholder="0"
                    />
                    {errors.regularPrice && <p className="text-xs text-danger mt-1">{errors.regularPrice.message}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1">Sale Price (PKR)</label>
                    <p className="text-2xs text-muted-foreground mb-1.5">Leave blank if no discount</p>
                    <input
                      {...register('salePrice')}
                      type="number"
                      className="input-field tabular-nums"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1">Cost Price (PKR)</label>
                    <p className="text-2xs text-muted-foreground mb-1.5">Private — for margin tracking</p>
                    <input
                      {...register('costPrice')}
                      type="number"
                      className="input-field tabular-nums"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1">
                      Stock Quantity <span className="text-danger">*</span>
                    </label>
                    <input
                      {...register('stockQty', { required: 'Stock quantity is required' })}
                      type="number"
                      className="input-field tabular-nums"
                      placeholder="0"
                    />
                    {errors.stockQty && <p className="text-xs text-danger mt-1">{errors.stockQty.message}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1">Low Stock Threshold</label>
                    <p className="text-2xs text-muted-foreground mb-1.5">Alert when stock drops below this</p>
                    <input
                      {...register('lowStockThreshold')}
                      type="number"
                      className="input-field tabular-nums"
                      placeholder="10"
                    />
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-muted/50 border border-border">
                  <p className="text-xs font-semibold text-foreground mb-3">Inventory Options</p>
                  <div className="space-y-2">
                    {[
                      { id: 'opt-backorder', label: 'Allow Backorders', desc: 'Customers can order even when out of stock' },
                      { id: 'opt-continue', label: 'Continue Selling When Out of Stock', desc: 'Show product as available regardless of stock' },
                    ].map((opt) => (
                      <label key={opt.id} className="flex items-start gap-3 cursor-pointer">
                        <input type="checkbox" className="mt-0.5 accent-primary w-4 h-4" />
                        <div>
                          <p className="text-xs font-medium">{opt.label}</p>
                          <p className="text-2xs text-muted-foreground">{opt.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Tab: Media */}
            {activeTab === 'tab-media' && (
              <>
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1">
                    Main Product Image <span className="text-danger">*</span>
                  </label>
                  <p className="text-2xs text-muted-foreground mb-2">Minimum 800×800px, JPG/PNG/WebP, max 5MB</p>
                  <div className="border-2 border-dashed border-border rounded-xl p-8 text-center hover:border-primary/50 transition-colors cursor-pointer">
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center mx-auto mb-2">
                      <ChevronRight size={18} className="text-muted-foreground" />
                    </div>
                    <p className="text-sm font-medium text-foreground">Drop main image here</p>
                    <p className="text-xs text-muted-foreground mt-1">or click to browse files</p>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1">Additional Images</label>
                  <p className="text-2xs text-muted-foreground mb-2">Up to 10 additional images — drag to reorder</p>
                  <div className="grid grid-cols-5 gap-2">
                    {Array.from({ length: 5 }, (_, i) => (
                      <div
                        key={`img-slot-${i + 1}`}
                        className="aspect-square rounded-lg border-2 border-dashed border-border flex items-center justify-center text-muted-foreground hover:border-primary/50 transition-colors cursor-pointer"
                      >
                        <span className="text-2xs">+</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1">Product Video URL</label>
                  <p className="text-2xs text-muted-foreground mb-1.5">YouTube or Vimeo URL — auto-embedded on product page</p>
                  <input
                    type="url"
                    className="input-field"
                    placeholder="https://youtube.com/watch?v=..."
                  />
                </div>
              </>
            )}

            {/* Tab: Variations */}
            {activeTab === 'tab-variations' && (
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-muted/50 border border-border">
                  <p className="text-xs font-semibold text-foreground mb-1">Variation Types</p>
                  <p className="text-2xs text-muted-foreground mb-3">Add attributes like Color, Size, Storage. System auto-generates combinations.</p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {['Color', 'Size', 'Storage', 'Flavor'].map((attr) => (
                      <span
                        key={`attr-${attr.toLowerCase()}`}
                        className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-secondary text-primary text-xs font-medium cursor-pointer hover:bg-secondary/80"
                      >
                        {attr}
                        <X size={11} />
                      </span>
                    ))}
                    <button className="px-2.5 py-1 rounded-full border border-dashed border-border text-xs text-muted-foreground hover:border-primary hover:text-primary transition-colors">
                      + Add Attribute
                    </button>
                  </div>
                </div>

                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-xs">Add variation types above, then click</p>
                  <button className="btn-secondary text-xs mt-2">Generate Combinations</button>
                </div>
              </div>
            )}

            {/* Tab: Status */}
            {activeTab === 'tab-status' && (
              <>
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1">Listing Status</label>
                  <p className="text-2xs text-muted-foreground mb-2">Published products are visible to customers immediately</p>
                  <div className="space-y-2">
                    {[
                      { value: 'published', label: 'Published', desc: 'Live on marketplace — visible to all customers' },
                      { value: 'draft', label: 'Draft', desc: 'Saved but not visible — complete setup before publishing' },
                      { value: 'pending_review', label: 'Submit for Review', desc: 'Send to Arain7 team for approval before going live' },
                    ].map((s) => (
                      <label
                        key={`status-opt-${s.value}`}
                        className="flex items-start gap-3 p-3 rounded-lg border border-border hover:border-primary/30 cursor-pointer transition-colors"
                      >
                        <input
                          type="radio"
                          value={s.value}
                          {...register('status')}
                          className="mt-0.5 accent-primary"
                        />
                        <div>
                          <p className="text-xs font-semibold">{s.label}</p>
                          <p className="text-2xs text-muted-foreground">{s.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-accent/30 cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      {...register('featured')}
                      className="accent-accent w-4 h-4"
                    />
                    <div>
                      <p className="text-xs font-semibold">Mark as Featured Product</p>
                      <p className="text-2xs text-muted-foreground">Featured products appear in homepage spotlights and category banners</p>
                    </div>
                  </label>
                </div>
              </>
            )}
          </div>

          {/* Sticky footer */}
          <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 flex items-center justify-between shrink-0">
            <button type="button" onClick={onClose} className="btn-ghost">
              Cancel
            </button>
            <div className="flex items-center gap-3">
              <button type="button" className="btn-secondary text-xs">
                Save as Draft
              </button>
              <button
                type="submit"
                disabled={submitting || saved}
                className="btn-primary text-xs min-w-[120px] justify-center"
              >
                {submitting ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Saving...
                  </>
                ) : saved ? (
                  <>
                    <Check size={14} />
                    Saved!
                  </>
                ) : (
                  'Add Product'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}